import { ampli } from '@/ampli';
import { PickAnimated, SpringValues, useSpring } from '@react-spring/web';
import { useEffect, useRef, useState } from 'react';

import { useMatchaWallets } from '@/hooks/useMatchaWallets';
import { useShouldShowFeedbackToast } from '@/hooks/useShouldShowFeedbackToast';

interface UseFeedbackToastAnimationOptions {
  /** Whether the transaction/order was successful */
  isSuccess: boolean;
  /** Callback when the entire toast should be dismissed */
  onClose?: () => void;
  /** Transaction hash for tracking */
  transactionHash?: string;
  /** Chain ID for tracking */
  chainId: number;
}

interface UseFeedbackToastAnimationReturn {
  feedbackToastSpring: SpringValues<PickAnimated<object>>;
  mainToastSpring: SpringValues<PickAnimated<object>>;
  showFeedbackToast: boolean;
  showMainToast: boolean;
  showFeedbackModal: boolean;
  handleFeedbackToastClose: () => void;
  handleFeedbackClick: () => void;
  handleMainToastClose: () => void;
  handleFeedbackModalClose: () => void;
  setShowFeedbackModal: (show: boolean) => void;
}

/**
 * Custom hook to manage feedback toast animation and state
 * Handles the slide-down + fade animation matching Sonner's default behavior
 */
export function useFeedbackToastAnimation({
  isSuccess,
  onClose,
  transactionHash,
  chainId,
}: UseFeedbackToastAnimationOptions): UseFeedbackToastAnimationReturn {
  const [showFeedbackToast, setShowFeedbackToast] = useState(false);
  const [showMainToast, setShowMainToast] = useState(true);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackToastVisible, setFeedbackToastVisible] = useState(false);
  const [mainToastVisible, setMainToastVisible] = useState(true);

  const feedbackToastTriggeredRef = useRef(false);
  const { ethereumWallet, solanaWallet } = useMatchaWallets();
  const walletAddress = ethereumWallet?.address || solanaWallet?.address;

  // Only fetch interaction state when transaction succeeds
  const { data: interactionState } = useShouldShowFeedbackToast(
    isSuccess ? walletAddress : undefined,
  );

  // Springs for animating toasts in/out - match Sonner's default animation (slide down + fade)
  const feedbackToastSpring = useSpring({
    opacity: feedbackToastVisible ? 1 : 0,
    transform: feedbackToastVisible ? 'translateY(0px)' : 'translateY(100%)',
    config: { tension: 200, friction: 20 },
  });

  const mainToastSpring = useSpring({
    opacity: mainToastVisible ? 1 : 0,
    transform: mainToastVisible ? 'translateY(0px)' : 'translateY(100%)',
    config: { tension: 200, friction: 20 },
  });

  // Show feedback toast when conditions are met
  useEffect(() => {
    if (feedbackToastTriggeredRef.current) return;
    if (!isSuccess) return;
    if (!interactionState?.shouldShowToast) return;

    feedbackToastTriggeredRef.current = true;
    setShowFeedbackToast(true);
    setFeedbackToastVisible(true);
    ampli.feedbackToastDisplayed({ chain_id: chainId, surface: 'swap' });
  }, [isSuccess, interactionState, transactionHash, chainId]);

  // Handle main toast close - animate out then close
  const handleMainToastClose = () => {
    // Start fade out animation (matches Sonner's default animation)
    setMainToastVisible(false);
    // Wait for fade animation to complete, then remove from DOM
    setTimeout(() => {
      setShowMainToast(false);
      // If feedback is not showing and modal is closed, dismiss entire Sonner toast
      if (!showFeedbackToast && !showFeedbackModal) {
        onClose?.();
      }
    }, 300); // Match spring animation duration
  };

  // Handle feedback toast close - animate out then close
  const handleFeedbackToastClose = () => {
    // Start fade out animation (matches Sonner's default animation)
    setFeedbackToastVisible(false);
    // Wait for fade animation to complete, then remove from DOM
    setTimeout(() => {
      setShowFeedbackToast(false);
      // If main toast is hidden and modal is closed, dismiss entire Sonner toast
      if (!showMainToast && !showFeedbackModal) {
        onClose?.();
      }
    }, 300); // Match spring animation duration
    ampli.feedbackToastDismissed({ chain_id: chainId, surface: 'swap' });
  };

  // Handle feedback button click - animate out then show modal
  const handleFeedbackClick = () => {
    ampli.feedbackToastClicked({ chain_id: chainId, surface: 'swap' });
    // Start fade out animation (matches Sonner's default animation)
    setFeedbackToastVisible(false);
    // Wait for fade animation to complete, then show modal
    setTimeout(() => {
      setShowFeedbackToast(false);
      setShowFeedbackModal(true);
    }, 300); // Match spring animation duration
    ampli.feedbackModalDisplayed();
  };

  // Handle feedback modal close
  const handleFeedbackModalClose = () => {
    setShowFeedbackModal(false);
    // If both toasts are hidden, dismiss entire Sonner toast
    if (!showMainToast && !showFeedbackToast) {
      onClose?.();
    }
  };

  return {
    feedbackToastSpring,
    mainToastSpring,
    showFeedbackToast,
    showMainToast,
    showFeedbackModal,
    handleFeedbackToastClose,
    handleFeedbackClick,
    handleMainToastClose,
    handleFeedbackModalClose,
    setShowFeedbackModal,
  };
}
